import React, { useMemo } from 'react';
import { Tabs, Tab, Tooltip, Typography, Grid, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { t } from 'ttag';
import DayPicker from 'react-day-picker';
import { format } from 'date-fns';
import 'react-day-picker/lib/style.css';
import { DaysList } from '@coolremote/shared';
import { getDateFnsLocale } from '@utils/dateUtils';
import addEditScheduleStyles from '../addEditSchedule.style';
import { useStoreState } from '../../../models/RootStore';

const useDefaultStyles = makeStyles(addEditScheduleStyles);

interface DaysDatePickerProps {
  /** The currently active tab (Weekly or Calendar) */
  tab: number;
  /** Callback function when the tab changes */
  onTabChange: (newValue: number) => void;
  /** Array of currently selected weekdays (as strings '0'-'6') */
  days: string[];
  /** Callback function to add or remove a weekday */
  onAddRemoveDay: (day: string) => void;
  /** Array of currently selected calendar dates */
  dates: Date[];
  /** Callback function when a day is clicked in the calendar */
  onDayClick: (day: Date, modifiers: any) => void;
  /** Whether the component is in edit mode */
  editMode?: boolean;
  /** Optional classes to override default styles */
  classes?: any;
}

/**
 * DaysDatePicker component for selecting weekly days or specific calendar dates.
 * Standardizes the logic for date/day selection across the application.
 */
const DaysDatePicker: React.FC<DaysDatePickerProps> = ({
  tab,
  onTabChange,
  days,
  onAddRemoveDay,
  dates,
  onDayClick,
  editMode = false,
  classes: providedClasses,
}) => {
  const scheduleCategories = useStoreState((state) => state.types.scheduleCategories);
  const weekDays = useStoreState((state) => state.types.weekDays);
  const weekDaysArray: string[] = useMemo(() => Object.values(weekDays), [weekDays]);
  const defaultClasses = useDefaultStyles();
  const classes = providedClasses || defaultClasses;

  // Internal calculations for date selection logic
  const today = new Date();
  const aYearFromToday = new Date();
  aYearFromToday.setFullYear(today.getFullYear() + 1);

  const isWeeklyTabDisabled = tab !== scheduleCategories.weekly && dates && dates.length > 0;
  const isCalendarTabDisabled = tab !== scheduleCategories.calendar && days && days.length > 0;

  const renderTabWithTooltip = ({
    value,
    label,
    className,
    disabled,
    tooltip,
  }: {
    value: number;
    label: React.ReactNode;
    className?: string;
    disabled: boolean;
    tooltip?: string;
  }) => {
    if (disabled && tooltip) {
      return (
        <Tooltip title={tooltip} placement="top" arrow>
          <span style={{ pointerEvents: 'auto', display: 'flex', width: '50%' }}>
            <Tab value={value} label={label} className={className} disabled />
          </span>
        </Tooltip>
      );
    }
    return <Tab value={value} label={label} className={className} disabled={disabled} />;
  };

  return (
    <div className={clsx(classes.container, classes.daysRow)}>
      <Tabs
        value={tab}
        onChange={(event: any, newValue: number) => onTabChange(newValue)}
        variant="fullWidth"
        aria-label="schedule category tabs"
        classes={{ root: classes.tabRoot }}
      >
        {renderTabWithTooltip({
          value: scheduleCategories.weekly,
          label: t`Weekly`,
          className: clsx(classes.tab, classes.weeklyTab),
          disabled: isWeeklyTabDisabled,
          tooltip: isWeeklyTabDisabled
            ? t`Remove all selected dates in Calendar to enable Weekly scheduling.`
            : undefined,
        })}
        {renderTabWithTooltip({
          value: scheduleCategories.calendar,
          label: t`Calendar`,
          className: clsx(classes.tab, classes.calendarTab),
          disabled: isCalendarTabDisabled,
          tooltip: isCalendarTabDisabled
            ? t`Remove all selected days in Weekly to enable Calendar scheduling.`
            : undefined,
        })}
      </Tabs>
      <div className={classes.tabContent}>
        <Typography className={clsx(classes.selectModeStyle, classes.selectModeLabel)}>
          {tab === scheduleCategories.weekly ? t`Choose Days` : t`Choose Dates`}
        </Typography>
        <Grid container className={classes.daysContainer} id="days">
          {tab === scheduleCategories.weekly ? (
            <div style={{ paddingBottom: 10 }}>
              <DaysList
                days={weekDaysArray}
                activeDays={days}
                action={onAddRemoveDay}
              />
            </div>
          ) : (
            <DayPicker
              selectedDays={dates}
              onDayClick={onDayClick}
              className={classes.calendar}
              initialMonth={dates.length > 0 ? dates[0] : (editMode ? undefined : today)}
              disabledDays={{
                before: editMode ? undefined : today,
                after: aYearFromToday,
              }}
              fromMonth={editMode ? undefined : today}
              toMonth={aYearFromToday}
              locale={getDateFnsLocale()}
              months={Array.from({ length: 12 }, (_, i) =>
                format(new Date(0, i), 'MMMM', {
                  locale: getDateFnsLocale(),
                })
              )}
              weekdaysLong={Array.from({ length: 7 }, (_, i) =>
                format(new Date(2023, 0, i + 1), 'EEEE', {
                  locale: getDateFnsLocale(),
                })
              )}
              weekdaysShort={Array.from({ length: 7 }, (_, i) =>
                format(new Date(2023, 0, i + 1), 'EEE', {
                  locale: getDateFnsLocale(),
                })
              )}
              firstDayOfWeek={1}
            />
          )}
        </Grid>
      </div>
    </div>
  );
};

export default DaysDatePicker;
